import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    import('@/lib/telemetry').then(({ reportError }) => {
      reportError(error, { componentStack: errorInfo.componentStack ?? undefined });
    }).catch(() => {
      // Telemetry unavailable
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[400px] items-center justify-center p-6">
          <div className="max-w-md text-center">
            <h2 className="font-zodiak text-xl font-bold text-ea-black">
              Something went wrong
            </h2>
            <p className="mt-2 text-sm text-ea-03">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            {this.state.error && (
              <p className="mt-2 rounded bg-ea-11 p-2 text-xs text-ea-03">
                {this.state.error.message}
              </p>
            )}
            <Button
              className="mt-4"
              onClick={() => {
                this.setState({ hasError: false, error: undefined });
                window.location.reload();
              }}
            >
              Refresh Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
